/**
 * Predictive Search — Live product suggestions as you type
 * Uses Shopify's /search/suggest.json endpoint (free, no app needed)
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var MIN_CHARS = 1;
  var MAX_RESULTS = 6;

  /* ── HTML escape ── */
  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  /* ── Format price (dollars string from suggest API → display) ── */
  function formatPrice(price) {
    var num = parseFloat(price);
    if (isNaN(num)) return '$0.00';
    var cents = Math.round(num * 100);
    return window.AykahFormatMoney ? window.AykahFormatMoney(cents) : '$' + num.toFixed(2);
  }

  /* ── Init each search wrapper ── */
  var wrappers = document.querySelectorAll('[data-predictive-search]');

  wrappers.forEach(function (wrapper) {
    var input = wrapper.querySelector('[data-predictive-search-input]');
    var results = wrapper.querySelector('[data-predictive-search-results]');
    var clearBtn = wrapper.querySelector('[data-predictive-search-clear]');
    if (!input || !results) return;

    var timer = null;
    var abortController = null;
    var cache = {};
    var activeIndex = -1;

    /* ── Debounced input handler ── */
    input.addEventListener('input', function () {
      var q = input.value.trim();

      /* Show/hide clear button */
      if (clearBtn) clearBtn.classList.toggle('hidden', q.length === 0);

      if (q.length < MIN_CHARS) {
        close();
        return;
      }

      clearTimeout(timer);
      timer = setTimeout(function () { fetchResults(q); }, DEBOUNCE_MS);
    });

    /* ── Clear button ── */
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.classList.add('hidden');
        close();
        input.focus();
      });
    }

    /* ── Keyboard navigation ── */
    input.addEventListener('keydown', function (e) {
      var items = results.querySelectorAll('[data-predictive-item]');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        highlightItem(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        highlightItem(items);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        var link = items[activeIndex].querySelector('a');
        if (link) window.location.href = link.href;
      } else if (e.key === 'Escape') {
        close();
      }
    });

    function highlightItem(items) {
      items.forEach(function (item, i) {
        if (i === activeIndex) {
          item.classList.add('bg-brand-warm/80');
          item.setAttribute('aria-selected', 'true');
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.classList.remove('bg-brand-warm/80');
          item.removeAttribute('aria-selected');
        }
      });
    }

    /* ── Fetch from Shopify Predictive Search API ── */
    function fetchResults(query) {
      /* Check cache */
      if (cache[query]) {
        render(cache[query], query);
        return;
      }

      /* Abort previous in-flight request */
      if (abortController) abortController.abort();
      abortController = new AbortController();

      showLoading();

      fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + MAX_RESULTS + '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor', {
        signal: abortController.signal,
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Search failed');
          return res.json();
        })
        .then(function (data) {
          var products = data.resources && data.resources.results && data.resources.results.products
            ? data.resources.results.products
            : [];
          cache[query] = products;
          render(products, query);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          close();
        });
    }

    /* ── Render results ── */
    function render(products, query) {
      activeIndex = -1;

      if (products.length === 0) {
        results.innerHTML =
          '<div class="px-5 py-8 text-center">' +
            '<svg class="w-10 h-10 mx-auto mb-3 text-brand-text-muted/30" viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"/></svg>' +
            '<p class="text-sm text-brand-text-muted">No results for "<span class="font-medium text-foreground">' + esc(query) + '</span>"</p>' +
            '<p class="text-xs text-brand-text-muted/60 mt-1">Try a different search term</p>' +
          '</div>';
        open();
        return;
      }

      var html = '<div class="max-h-[400px] overflow-y-auto overscroll-contain">';

      products.forEach(function (product, i) {
        var img = product.image || (product.featured_image && product.featured_image.url) || '';
        if (img && img.indexOf('?') === -1) img += '?width=120';
        else if (img) img += '&width=120';

        var price = product.price;
        var comparePrice = product.compare_at_price_max || product.compare_at_price;
        var hasDiscount = comparePrice && parseFloat(comparePrice) > parseFloat(price);

        html +=
          '<div class="group/result transition-colors hover:bg-brand-warm/60 cursor-pointer' + (i > 0 ? ' border-t border-brand-border/20' : '') + '" data-predictive-item role="option">' +
            '<a href="' + product.url + '" class="flex items-center gap-3.5 px-4 py-3" tabindex="-1">' +
              '<div class="flex-shrink-0 w-14 h-[70px] rounded-lg overflow-hidden bg-brand-warm">' +
                (img
                  ? '<img src="' + img + '" alt="' + esc(product.title) + '" class="w-full h-full object-cover" loading="lazy">'
                  : '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-brand-text-muted/20" viewBox="0 0 256 256" fill="currentColor"><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/></svg></div>'
                ) +
              '</div>' +
              '<div class="flex-1 min-w-0">' +
                '<p class="font-serif text-[13px] font-medium text-foreground leading-snug truncate group-hover/result:text-brand-accent-text transition-colors">' + highlightMatch(esc(product.title), query) + '</p>' +
                (product.product_type ? '<p class="text-[11px] text-brand-text-muted/60 mt-0.5 truncate">' + esc(product.product_type) + '</p>' : '') +
                '<div class="flex items-center gap-1.5 mt-1">' +
                  '<span class="text-[13px] font-semibold text-foreground">' + formatPrice(price) + '</span>' +
                  (hasDiscount ? '<span class="text-[11px] text-brand-text-muted line-through">' + formatPrice(comparePrice) + '</span>' : '') +
                '</div>' +
              '</div>' +
              '<svg class="w-4 h-4 text-brand-text-muted/30 flex-shrink-0 group-hover/result:text-brand-accent-text transition-colors" viewBox="0 0 256 256" fill="currentColor"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/></svg>' +
            '</a>' +
          '</div>';
      });

      html += '</div>';

      /* View all results link */
      html +=
        '<a href="/search?q=' + encodeURIComponent(window.aykahAndifyQuery ? window.aykahAndifyQuery(query) : query) + '" class="flex items-center justify-center gap-2 px-4 py-3 bg-brand-warm/40 text-[12px] font-semibold text-brand-accent-text uppercase tracking-wider hover:bg-brand-warm transition-colors border-t border-brand-border/20">' +
          'View all results' +
          '<svg class="w-3.5 h-3.5" viewBox="0 0 256 256" fill="currentColor"><path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"/></svg>' +
        '</a>';

      results.innerHTML = html;
      open();
    }

    /* ── Highlight matching text ── */
    function highlightMatch(escaped, query) {
      var words = query.trim().split(/\s+/);
      words.forEach(function (word) {
        if (!word) return;
        var re = new RegExp('(' + word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
        escaped = escaped.replace(re, '<mark class="bg-transparent text-brand-accent-text font-semibold">$1</mark>');
      });
      return escaped;
    }

    /* ── Loading skeleton ── */
    function showLoading() {
      var html = '';
      for (var i = 0; i < 3; i++) {
        html +=
          '<div class="flex items-center gap-3.5 px-4 py-3' + (i > 0 ? ' border-t border-brand-border/20' : '') + '">' +
            '<div class="flex-shrink-0 w-14 h-[70px] rounded-lg bg-brand-warm animate-pulse"></div>' +
            '<div class="flex-1">' +
              '<div class="h-3.5 w-3/4 bg-brand-warm rounded animate-pulse"></div>' +
              '<div class="h-3 w-1/3 bg-brand-warm rounded animate-pulse mt-2"></div>' +
            '</div>' +
          '</div>';
      }
      results.innerHTML = html;
      open();
    }

    /* ── Open / Close dropdown ── */
    function open() {
      results.classList.remove('hidden');
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      results.classList.add('hidden');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    /* ── Close on outside click ── */
    document.addEventListener('click', function (e) {
      if (!wrapper.contains(e.target)) close();
    });

    /* ── Close on Escape (also handled by header.js for mobile search) ── */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    /* ── Focus: show cached results if input has value ── */
    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= MIN_CHARS && cache[q]) {
        render(cache[q], q);
      }
    });
  });
})();

/* ──────────────────────────────────────────────
   SEARCH QUERY: AND, not OR
   Shopify's storefront search treats a multi-word query as OR and ranks by
   relevance, so "dining chairs" matched every product containing "dining" OR
   "chairs": 19 dining tables, 5 sofas and 3 counter stools came back alongside
   the chairs. Shopify honours an explicit AND, which drops those to zero while
   keeping its typo tolerance ("dinning chairs" still resolves).

   Applied at submit time on every search form so the header, the 404 page and
   the results page all behave the same. Quoted phrases and queries that already
   carry an operator are left exactly as typed.
   ────────────────────────────────────────────── */
(function () {
  var OPERATORS = /(^|\s)(AND|OR|NOT)(\s|$)/;

  function andify(raw) {
    var q = String(raw == null ? '' : raw).trim();
    if (!q) return q;
    if (q.indexOf('"') !== -1 || OPERATORS.test(q)) return q;
    var words = q.split(/\s+/).filter(Boolean);
    if (words.length < 2) return q;
    return words.join(' AND ');
  }

  window.aykahAndifyQuery = andify;

  function bind() {
    document.querySelectorAll('form[role="search"], form[action$="/search"]').forEach(function (form) {
      if (form.dataset.andifyBound) return;
      form.dataset.andifyBound = '1';
      form.addEventListener('submit', function () {
        var input = form.querySelector('input[name="q"]');
        if (input) input.value = andify(input.value);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);
})();
